import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Divider, Stack } from '@mui/material';
import { Inputs } from 'components/form-control';
import yup from 'config/yup.custom';
import { NotifyService } from 'helpers';
import { UPDATE_CONCIERGE_BODY } from 'models';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { getSurchargesConciergeApi, updateConfigConciergeApi } from 'services';
import { setGlobalLoading } from 'store/reducers/global';

interface IFormInputs {
  additional_child_surcharge_for_sitter: number;
  odd_hour_surcharge_for_sitter: number;
  weekend_surcharge_for_sitter: number;
  public_holiday_surcharge_for_sitter: number;
  additional_child_surcharge_for_parent: number;
  odd_hour_surcharge_for_parent: number;
  weekend_surcharge_for_parent: number;
  public_holiday_surcharge_for_parent: number;
  percentage_fee: number;
}

const defaultValues: Partial<IFormInputs> = {
  additional_child_surcharge_for_sitter: 0,
  odd_hour_surcharge_for_sitter: 0,
  weekend_surcharge_for_sitter: 0,
  public_holiday_surcharge_for_sitter: 0,
  additional_child_surcharge_for_parent: 0,
  odd_hour_surcharge_for_parent: 0,
  weekend_surcharge_for_parent: 0,
  public_holiday_surcharge_for_parent: 0,
  percentage_fee: 0,
};

const Surcharge = () => {
  const schema = useMemo(() => {
    return yup.object({
      additional_child_surcharge_for_sitter: yup
        .number()
        .min(0)
        .required('Additional child surcharge for sitter is required'),
      odd_hour_surcharge_for_sitter: yup
        .number()
        .min(0)
        .required('Odd hour surcharge for sitter is required'),
      weekend_surcharge_for_sitter: yup
        .number()
        .min(0)
        .required('Weekend surcharge for sitter is required'),
      public_holiday_surcharge_for_sitter: yup
        .number()
        .min(0)
        .required('Public holiday surcharge for sitter is required'),
      additional_child_surcharge_for_parent: yup
        .number()
        .min(0)
        .required('Additional child surcharge for parent is required'),
      odd_hour_surcharge_for_parent: yup
        .number()
        .min(0)
        .required('Odd hour surcharge for parent is required'),
      weekend_surcharge_for_parent: yup
        .number()
        .min(0)
        .required('Weekend surcharge for parent is required'),
      public_holiday_surcharge_for_parent: yup
        .number()
        .min(0)
        .required('Public holiday surcharge for parent is required'),
      percentage_fee: yup
        .number()
        .min(0)
        .required('Credit Card Fee is required'),
    });
  }, []);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: defaultValues,
  });

  const { reset, handleSubmit } = form;

  const fetchData = async () => {
    setGlobalLoading(true);
    await getSurchargesConciergeApi()
      .then(({ data: { data } }) => {
        reset({
          additional_child_surcharge_for_sitter:
            data.additional_child_surcharge_for_sitter,
          odd_hour_surcharge_for_sitter: data.odd_hour_surcharge_for_sitter,
          weekend_surcharge_for_sitter: data.weekend_surcharge_for_sitter,
          public_holiday_surcharge_for_sitter:
            data.public_holiday_surcharge_for_sitter,
          additional_child_surcharge_for_parent:
            data.additional_child_surcharge_for_parent,
          odd_hour_surcharge_for_parent: data.odd_hour_surcharge_for_parent,
          weekend_surcharge_for_parent: data.weekend_surcharge_for_parent,
          public_holiday_surcharge_for_parent:
            data.public_holiday_surcharge_for_parent,
          percentage_fee: data.percentage_fee,
        });
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setGlobalLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (value: IFormInputs) => {
    const data: UPDATE_CONCIERGE_BODY = {
      additional_child_surcharge_for_sitter:
        value.additional_child_surcharge_for_sitter,
      odd_hour_surcharge_for_sitter: value.odd_hour_surcharge_for_sitter,
      weekend_surcharge_for_sitter: value.weekend_surcharge_for_sitter,
      public_holiday_surcharge_for_sitter:
        value.public_holiday_surcharge_for_sitter,
      additional_child_surcharge_for_parent:
        value.additional_child_surcharge_for_parent,
      odd_hour_surcharge_for_parent: value.odd_hour_surcharge_for_parent,
      weekend_surcharge_for_parent: value.weekend_surcharge_for_parent,
      public_holiday_surcharge_for_parent:
        value.public_holiday_surcharge_for_parent,
      percentage_fee: value.percentage_fee,
    };
    setGlobalLoading(true);
    await updateConfigConciergeApi(data)
      .then((r) => {
        fetchData();
        NotifyService.success(r);
      })
      .catch((e) => NotifyService.error(e))
      .finally(() => {
        setGlobalLoading(false);
      });
  };

  return (
    <Box onSubmit={handleSubmit(onSubmit)} component="form">
      <Stack sx={{ width: '600px' }} spacing="16px">
        <Stack direction="row" spacing={2}>
          <Stack spacing="16px" width="100%">
            <Stack>
              <Inputs
                type="number"
                title="Additional child surcharge for sitter"
                form={form}
                name="additional_child_surcharge_for_sitter"
              />
            </Stack>
            <Stack>
              <Inputs
                type="number"
                title="Odd hour surcharge for sitter"
                form={form}
                name="odd_hour_surcharge_for_sitter"
              />
            </Stack>
            <Stack>
              <Inputs
                title="Weekend surcharge for sitter"
                form={form}
                name="weekend_surcharge_for_sitter"
                type="number"
              />
            </Stack>
            <Stack>
              <Inputs
                title="Public holiday surcharge for sitter"
                form={form}
                name="public_holiday_surcharge_for_sitter"
                type="number"
              />
            </Stack>
          </Stack>
          <Stack spacing="16px" width="100%">
            <Stack>
              <Inputs
                type="number"
                title="Additional child surcharge for parent"
                form={form}
                name="additional_child_surcharge_for_parent"
              />
            </Stack>
            <Stack>
              <Inputs
                type="number"
                title="Odd hour surcharge for parent"
                form={form}
                name="odd_hour_surcharge_for_parent"
              />
            </Stack>
            <Stack>
              <Inputs
                title="Weekend surcharge for parent"
                form={form}
                name="weekend_surcharge_for_parent"
                type="number"
              />
            </Stack>
            <Stack>
              <Inputs
                title="Public holiday surcharge for parent"
                form={form}
                name="public_holiday_surcharge_for_parent"
                type="number"
              />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
        <Stack>
          <Inputs
            title="Credit Card Fee"
            form={form}
            name="percentage_fee"
            type="number"
          />
        </Stack>
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          sx={{
            height: '30px',
          }}
          type="submit"
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default Surcharge;
